import {Component, EffectRef, InputSignal, OutputEmitterRef, Signal, WritableSignal, computed, effect, inject, input, output, signal, untracked} from '@angular/core';
import {Dialog, DialogModule, TooltipModule} from "@syncfusion/ej2-angular-popups";
import {FormInputComponent} from "../../../shared/elements/form-input/form-input.component";
import {TranslatePipe} from "../../../shared/pipes/translate.pipe";
import {FormBuilder, FormGroup} from "@angular/forms";
import {DialogService} from "../../../shared/services/dialog.service";
import {LanguageService} from "../../../shared/services/language.service";
import {HeadlineComponent} from "../../../shared/elements/headline/headline.component";
import {TooltipComponent} from "../../../shared/elements/tooltip/tooltip.component";
import {DatePipe} from "@angular/common";
import {DialogHomeLinkComponent} from "../dialog-home-link/dialog-home-link.component";
import {IconBadgeComponent} from "../../../shared/elements/icon-badge/icon-badge.component";
import {LinkComponent} from "../../../shared/elements/link/link.component";
import {DialogUtility} from "@syncfusion/ej2-popups";
import {FormButtonComponent} from "../../../shared/elements/form-button/form-button.component";
import {ApiService} from "../../../shared/services/api.service";
import {take} from "rxjs";
import {ToasterService} from '../../../shared/services/toaster.service';
import {DashboardLinkAdminResource} from '../../../shared/types/generated/dashboardLinkAdminResource';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {RouterService} from '../../../shared/services/router.service';

@Component({
    selector: 'app-dialog-home-link-manager',
    imports: [
        DialogModule,
        FormInputComponent,
        TooltipModule,
        TranslatePipe,
        HeadlineComponent,
        TooltipComponent,
        DialogHomeLinkComponent,
        IconBadgeComponent,
        LinkComponent,
        DatePipe,
        FormButtonComponent
    ],
    templateUrl: './dialog-home-link-manager.component.html',
    styleUrl: './dialog-home-link-manager.component.scss'
})
export class DialogHomeLinkManagerComponent {
  public dialogService: DialogService = inject(DialogService);
  private languageService: LanguageService = inject(LanguageService);
  private formBuilder: FormBuilder = inject(FormBuilder);
  private apiService: ApiService = inject(ApiService);
  private toaster: ToasterService = inject(ToasterService);
  private routerService: RouterService = inject(RouterService);

  public visible: InputSignal<boolean> = input(false);
  public visibleChange: OutputEmitterRef<boolean> = output<boolean>();

  public homeLinksForm: FormGroup = this.formBuilder.group({
    searchString: [''],
  });
  public homeLinks: WritableSignal<DashboardLinkAdminResource[]> = signal<DashboardLinkAdminResource[]>([]);
  public query: WritableSignal<string> = signal<string>('');
  public homeLinkToEdit: DashboardLinkAdminResource | null = null;
  public homeLinkDialog: WritableSignal<boolean> = signal<boolean>(false);
  private dialogInstance: Dialog | null = null;

  public constructor() {
    this.homeLinksForm.get('searchString')?.valueChanges
    .pipe(takeUntilDestroyed())
    .subscribe((value) => {
      this.query.set(value);
    });
  }

  private visibleEffect: EffectRef = effect(() => {
    if(this.visible()) {
      untracked(() => {
        this.loadSettingsLinks();
      })
    }
  });

  private editDialogEffect: EffectRef = effect(() => {
    if(!this.homeLinkDialog()) {
      this.homeLinkToEdit = null;
      this.loadSettingsLinks();
    }
  });

  public displayLinks: Signal<DashboardLinkAdminResource[]> = computed(() => {
    const query = this.query().toLowerCase();
    return this.homeLinks().filter(link => link.name.toLowerCase().includes(query));
  });

  public resetList(): void {
    this.homeLinksForm.controls['searchString'].setValue('');
  }

  public close(): void {
    this.homeLinks.set([]);
    this.query.set('');
    this.homeLinkToEdit = null;
    this.visibleChange.emit(false);
  }

  public edit(homeLink: DashboardLinkAdminResource): void {
    this.homeLinkToEdit = homeLink;
    this.homeLinkDialog.set(true);
  }

  public showDeleteOverlay(linkId: number): void {
    this.dialogInstance = DialogUtility.confirm({
      title: '<h6 class="text-danger fw-bold m-0 pt-1">' + this.languageService.getTranslationByKey('CONFIRMITEMDELETE') + '</h6>',
      content: this.languageService.getTranslationByKey('AREYOUSUREYOUWANTTODELETE'),
      cancelButton: {
        text: this.languageService.getTranslationByKey('CANCEL'),
        click: () => this.showDeleteOverlayCancel()
      },
      okButton: {
        text: this.languageService.getTranslationByKey('DELETE'),
        click: () => this.showDeleteOverlayConfirmed(linkId),
        cssClass: 'bg-danger brd-danger'
      },
      showCloseIcon: true,
      closeOnEscape: true,
      position: {X: 'center', Y: 'center'},
      animationSettings: {effect: 'Fade'},
      zIndex: 9999
    });
  }

  private showDeleteOverlayConfirmed(linkId: number): void {
    this.apiService.removeSettingsHomeLink(linkId)
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.toaster.success(
          this.languageService.getTranslationByKey('SETTINGSLINKDELETESUCCESS'),
          this.languageService.getTranslationByKey('SETTINGSLINKDELETESUCCESSMESSAGE')
        );
        this.loadSettingsLinks();
        this.dialogInstance?.hide();
      },
      error: (err) => {
        console.error(err);
        this.loadSettingsLinks();
        this.dialogInstance?.hide();
      }
    });
  }

  private showDeleteOverlayCancel(): void {
    this.dialogInstance?.hide();
  }

  public loadSettingsLinks(): void {
    this.apiService.getDashboardHomeLinks()
    .pipe(take(1))
    .subscribe({
      next: (links) => {
        this.homeLinks.set(links);
        console.log(links);
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  public moveLink(linkId: number, direction: 'up' | 'down'): void {
    this.apiService.moveSettingsHomeLink(linkId, direction)
    .pipe(take(1))
    .subscribe({
      next: () => {
        this.loadSettingsLinks();
      },
      error: (err) => {
        console.error(err);
      }
    });
  }

  public platformName(homeLink: DashboardLinkAdminResource): string {
    if(homeLink.on_web && homeLink.on_mobile) {
      return 'BOTH';
    } else if(homeLink.on_web) {
      return 'WEB';
    } else if(homeLink.on_mobile) {
      return 'MOBILE';
    } else {
      return 'NONE';
    }
  }

  public linkTypeName(homeLink: DashboardLinkAdminResource): string {
    try {
      const currentHost = window.location.hostname;
      const urlHost = new URL(homeLink.url, window.location.origin).hostname;
      if (currentHost === urlHost || this.routerService.checkIfUrlIsCustomerForm(homeLink.url)) {
        return 'INTERNAL';
      } else {
        return 'EXTERNAL';
      }
    } catch (e) {
      console.error(e);
      // ignore invalid URLs
    }
    return 'EXTERNAL';
  }
}
